import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Tag, VoucherType } from '../../../types'
import { FaCaretDown, FaCaretRight } from 'react-icons/fa'
import VoucherStateBadge from '../VoucherStateBadge'
import VoucherCommentsBadge from '../VoucherCommentsBadge'
import PaymentAccountBadges from '../PaymentAccountBadges'
import { formatPaymentDate, formatPeriod } from '../../../utils/utils'
import { Link } from 'react-router-dom'
import theme from '../../../theme'
import TagChips from '../../Tags/TagChips'

export const getReportsColumns = (
  isNationalMaal: boolean = false,
  onTagClick?: (tag: Tag) => void,
): ColumnDef<VoucherType>[] => [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) =>
      row.getCanExpand() ? (
        <button
          onClick={row.getToggleExpandedHandler()}
          style={{ cursor: 'pointer', border: 'none', background: 'none' }}
        >
          {row.getIsExpanded() ? (
            <FaCaretDown color={theme.palette.primary.main} />
          ) : (
            <FaCaretRight color={theme.palette.primary.main} />
          )}
        </button>
      ) : null,
    size: 40,
  },
  {
    accessorKey: 'reference_number',
    header: 'Reference #',
    cell: ({ row }) => {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <PaymentAccountBadges voucher={row.original} />
          <Link to={`/panel/reports/${row.original.id}/edit`}>
            {row.original.reference_number ?? 'Draft'}
          </Link>
          <VoucherCommentsBadge voucher={row.original} />
        </div>
      )
    },
    enableColumnFilter: false,
    size: 20,
  },
  {
    accessorFn: (row) =>
      row.submitted_at
        ? new Date((row.submitted_at as number) * 1).toISOString().split('T')[0]
        : '',
    header: 'Submitted At',
    enableColumnFilter: false,
    size: 80,
  },
  {
    accessorFn: (row) => row.department.name,
    header: 'Department',
    enableColumnFilter: false,
    size: 20,
  },
  {
    accessorFn: (row) =>
      row.created_by.first_name + ' ' + row.created_by.last_name,
    header: 'Prepared by',
    enableColumnFilter: false,
    size: 200,
  },
  {
    accessorKey: 'state',
    header: 'State',
    cell: ({ row }) => <VoucherStateBadge state={row.original.state} />,
    enableColumnFilter: false,
    size: 10,
  },
  {
    id: 'tags',
    header: 'Tags',
    cell: ({ row }) => (
      <TagChips tags={row.original.tags} onTagClick={onTagClick} />
    ),
    enableColumnFilter: false,
    size: 120,
  },
  {
    accessorFn: (row) => formatPaymentDate(row.paid_by_amin),
    header: 'Payment',
    enableColumnFilter: false,
    size: 80,
  },
  {
    accessorFn: (row) => formatPeriod(row.expense_period),
    header: 'Expense Month',
    enableColumnFilter: false,
    size: 80,
  },
  {
    header: 'Cost Centers',
    cell: ({ row }) => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        {row.original.voucher_cost_category.map((cat, index) => (
          <div key={cat.id || index}>
            <div style={{ fontWeight: 'bold' }}>
              {cat.sub_cost_category.cost_category?.name}
            </div>
            <div style={{ paddingLeft: '10px' }}>
              {cat.sub_cost_category.name} ({cat.sub_cost_category.code}) :{' '}
              {cat.cost_share} {row.original.currency}
            </div>
          </div>
        ))}
      </div>
    ),
    enableColumnFilter: false,
    size: 300,
  },
  {
    accessorFn: (row) =>
      new Intl.NumberFormat('en-SE', {
        style: 'currency',
        currency: row.currency,
      }).format(row.total_amount),
    header: 'Amount',
    enableColumnFilter: false,
    meta: { align: 'right' },
    size: 120,
  },
]
